/**
 * Live open/closed status from the CMS's free-text opening times.
 *
 * Branch opening times are stored as rows of { period, status, published } typed freely in the admin,
 * e.g. "Mon - Fri:" / "08:30 - 17:30", "Sunday" / "Closed". This reads them best-effort and returns
 * null whenever today can't be determined with confidence, so callers hide the status rather than guess.
 */
(function (root) {
    const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    // "mon", "mon.", "monday", "mondays", "tues", "thurs" etc. -> 0-6
    function dayIndex(word) {
        const w = word.toLowerCase().replace(/[^a-z]/g, '');
        if (w.length < 2) return -1;
        return DAY_NAMES.findIndex(d => d.startsWith(w.replace(/s$/, '')) || d.startsWith(w) || w.startsWith(d));
    }

    // Period text -> array of weekday indexes, or null if it isn't recognisably about weekdays
    function parsePeriod(text) {
        if (!text) return null;
        const t = text.toLowerCase().replace(/[:.]/g, ' ').replace(/\s+/g, ' ').trim();
        if (/weekdays?/.test(t)) return [1, 2, 3, 4, 5];
        if (/weekends?/.test(t)) return [0, 6];
        if (/every ?day|daily|7 days/.test(t)) return [0, 1, 2, 3, 4, 5, 6];

        const days = new Set();
        // Ranges: "mon - fri", "monday to saturday", "mon–sat"
        const range = /([a-z]+)\s*(?:-|–|—|to|until|till)\s*([a-z]+)/g;
        let consumed = t, m;
        while ((m = range.exec(t))) {
            const a = dayIndex(m[1]), b = dayIndex(m[2]);
            if (a < 0 || b < 0) continue;
            for (let d = a; ; d = (d + 1) % 7) { days.add(d); if (d === b) break; }
            consumed = consumed.replace(m[0], ' ');
        }
        // Single days or lists: "saturday", "sat & sun", "sat, sun"
        consumed.split(/[\s,&/+]+|\band\b/).forEach(word => {
            const d = dayIndex(word);
            if (d >= 0) days.add(d);
        });
        return days.size ? [...days] : null;
    }

    // "08:30", "8.30am", "5pm", "17:30" -> minutes past midnight
    function parseTime(text) {
        const m = text.trim().toLowerCase().match(/^(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm)?$/);
        if (!m) return null;
        let h = +m[1];
        const min = m[2] ? +m[2] : 0;
        if (m[3] === 'pm' && h < 12) h += 12;
        if (m[3] === 'am' && h === 12) h = 0;
        if (h > 23 || min > 59) return null;
        return h * 60 + min;
    }

    // Status text -> { closed: true } | { open, close } | null if unreadable
    function parseStatus(text) {
        if (!text) return null;
        const t = text.toLowerCase().trim();
        if (/^closed\b/.test(t)) return { closed: true };
        const m = t.match(/^(\d{1,2}(?:[:.]\d{2})?\s*(?:am|pm)?)\s*(?:-|–|—|to|until|till)\s*(\d{1,2}(?:[:.]\d{2})?\s*(?:am|pm)?)$/);
        if (!m) return null;
        const open = parseTime(m[1]), close = parseTime(m[2]);
        if (open == null || close == null || close <= open) return null;
        return { open, close };
    }

    // Published rows, re-pairing a period left in one row with a status typed into the next
    function normaliseRows(rows) {
        const list = (Array.isArray(rows) ? rows : Object.values(rows || {}))
            .filter(r => r && String(r.published) !== '0')
            .map(r => ({ period: (r.period || '').trim(), status: (r.status || '').trim() }))
            .filter(r => r.period || r.status);
        const out = [];
        for (let i = 0; i < list.length; i++) {
            const row = list[i];
            if (row.period && !row.status && list[i + 1] && !list[i + 1].period && list[i + 1].status) {
                out.push({ period: row.period, status: list[i + 1].status });
                i++;
            } else if (row.period && row.status) {
                out.push(row);
            }
        }
        return out;
    }

    // rows -> week map [0..6] of { closed } | { open, close } | undefined (unknown) | 'conflict'
    function buildWeek(rows) {
        const week = new Array(7);
        normaliseRows(rows).forEach(({ period, status }) => {
            const days = parsePeriod(period);
            if (!days) return; // e.g. "Bank Hols": not a weekday rule, ignore
            const hours = parseStatus(status);
            days.forEach(d => {
                if (!hours) { week[d] = 'conflict'; return; }
                if (week[d] && JSON.stringify(week[d]) !== JSON.stringify(hours)) week[d] = 'conflict';
                else week[d] = hours;
            });
        });
        return week;
    }

    // Current day/minutes in UK time, whatever the visitor's own timezone
    function ukNow(date) {
        const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', weekday: 'long', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
            .formatToParts(date || new Date());
        const get = type => parts.find(p => p.type === type).value;
        return { day: DAY_LABELS.indexOf(get('weekday')), mins: +get('hour') * 60 + +get('minute') };
    }

    const fmt = m => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    const known = h => h && h !== 'conflict';

    /**
     * Returns { open: boolean, text: string } or null when today isn't known for certain.
     */
    function openingStatus(rows, date) {
        const week = buildWeek(rows);
        const { day, mins } = ukNow(date);
        const today = week[day];
        if (!known(today)) return null;

        if (!today.closed && mins >= today.open && mins < today.close) {
            return { open: true, text: `Open now · until ${fmt(today.close)}` };
        }
        if (!today.closed && mins < today.open) {
            return { open: false, text: `Closed · opens ${fmt(today.open)} today` };
        }
        for (let n = 1; n <= 7; n++) {
            const next = week[(day + n) % 7];
            if (!known(next)) return { open: false, text: 'Closed now' };
            if (!next.closed) {
                const when = n === 1 ? 'tomorrow' : DAY_LABELS[(day + n) % 7];
                return { open: false, text: `Closed · opens ${when} at ${fmt(next.open)}` };
            }
        }
        return { open: false, text: 'Closed now' };
    }

    root.OpeningStatus = { openingStatus, buildWeek, parsePeriod, parseStatus, normaliseRows };
    if (typeof module !== 'undefined') module.exports = root.OpeningStatus;
})(typeof window !== 'undefined' ? window : globalThis);
